import type { ReactNode } from 'react';
import { ArrowSquareOut, BookOpen, Certificate } from '@phosphor-icons/react';
import LinkingOptionButton from './LinkingOptionButton';
import { FRONTAPP_ARTICLES } from '../../../shared/constants/articles';
import type { HelpModalOption } from './orchestration/useLinkingTracking';

// Contenido reusable de "¿Cómo recupero mi contraseña del SAT?". Mata la
// triplicación que existía entre el branch 'link' de ModalFailed y el Drawer
// hand-rolled de LinkRfcStep. El contenedor (Modal / Drawer) lo pone el caller;
// aquí solo va el cuerpo. `onSwitchToEfirma` es opcional (solo ModalFailed, que
// puede cambiar de método, lo pasa).
type PasswordRecoveryContentProps = {
	onOpenSite: (url: string) => void;
	onSwitchToEfirma?: () => void;
	/** Avisa qué fila se tocó, antes de ejecutarla. Opcional: el Drawer de
	 * LinkRfcStep no lo pasa y sigue igual. */
	onOptionClick?: (option: HelpModalOption) => void;
	/** Fila que va PRIMERA en la lista de opciones (el video, en ModalFailed).
	 * Vive aquí y no en el caller para que el orden del bloque se lea en un
	 * solo lugar; el Drawer de LinkRfcStep no la pasa. */
	leadingOption?: ReactNode;
};

const PasswordRecoveryContent = ({
	onOpenSite,
	onSwitchToEfirma,
	onOptionClick,
	leadingOption,
}: PasswordRecoveryContentProps) => (
	<>
		<p className='text-h-text-medium text-sm leading-relaxed font-light'>
			La contraseña del SAT
			<b className='text-h-text-heading font-semibold'>
				{' '}
				debe tener al menos 8 caracteres y un número.
			</b>{' '}
			Te permite acceder a los servicios en línea del SAT, como presentar tu
			declaración anual, consultar facturas, etc.
		</p>
		<div className='flex flex-col gap-2.5'>
			{leadingOption}
			<LinkingOptionButton
				icon={<BookOpen size={20} weight='fill' />}
				text='Te compartimos un <b class="font-semibold">artículo que te ayudará</b> a conectar tu cuenta del SAT.'
				action={() => {
					onOptionClick?.('article');
					onOpenSite(FRONTAPP_ARTICLES.ciec);
				}}
			/>
			<LinkingOptionButton
				icon={<ArrowSquareOut size={20} weight='fill' />}
				text='Si aún <b class="font-semibold">no cuentas con tu contraseña o la has olvidado</b>, puedes generarla en el portal oficial del SAT.'
				action={() => {
					onOptionClick?.('sat_portal');
					onOpenSite('https://satid.sat.gob.mx/');
				}}
			/>
			{onSwitchToEfirma && (
				<LinkingOptionButton
					icon={<Certificate size={20} weight='fill' />}
					text='Recuerda que puedes vincular por <b class="font-semibold">e.firma</b> también.'
					action={() => {
						onOptionClick?.('switch_method');
						onSwitchToEfirma();
					}}
				/>
			)}
		</div>
	</>
);

export default PasswordRecoveryContent;
